import dayjs from "dayjs";

export function capitalizeAgendaLabel(text) {
  const s = String(text || "").trim();
  if (!s) return s;
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Título do período exibido na barra da agenda (dia, semana, mês ou lista). */
export function formatAgendaPeriodTitle(from, to, viewKey) {
  const start = dayjs(from);
  // FullCalendar `datesSet.end` is exclusive (often midnight of the next period).
  const end = dayjs(to).subtract(1, "millisecond");

  if (viewKey === "day") {
    return capitalizeAgendaLabel(start.format("dddd, D [de] MMMM [de] YYYY"));
  }

  if (viewKey === "month") {
    // Month grid `start` is the first visible cell (often the previous month).
    // Use the midpoint of the visible range so the title matches the current month.
    const mid = start.add(Math.max(0, Math.floor(end.diff(start, "day") / 2)), "day");
    return capitalizeAgendaLabel(mid.format("MMMM [de] YYYY"));
  }

  if (start.isSame(end, "day")) {
    return capitalizeAgendaLabel(start.format("D [de] MMMM [de] YYYY"));
  }

  if (start.isSame(end, "month") && start.isSame(end, "year")) {
    return capitalizeAgendaLabel(`${start.format("D")} – ${end.format("D [de] MMMM [de] YYYY")}`);
  }

  if (start.isSame(end, "year")) {
    return capitalizeAgendaLabel(
      `${start.format("D [de] MMM")} – ${end.format("D [de] MMM [de] YYYY")}`
    );
  }

  return capitalizeAgendaLabel(
    `${start.format("D [de] MMM [de] YYYY")} – ${end.format("D [de] MMM [de] YYYY")}`
  );
}

export function isAgendaCurrentPeriod(from, to, viewKey) {
  const now = dayjs();
  const start = dayjs(from);
  const end = dayjs(to).subtract(1, "millisecond");

  if (viewKey === "day") return now.isSame(start, "day");
  if (viewKey === "month") {
    const mid = start.add(Math.max(0, Math.floor(end.diff(start, "day") / 2)), "day");
    return now.isSame(mid, "month");
  }
  return !now.isBefore(start, "day") && !now.isAfter(end, "day");
}

export const AGENDA_SEGMENT = "/agenda";

export const AGENDA_SOURCE_KEYS = ["manual", "next_contact", "task", "google"];

export const AGENDA_SOURCE_COLORS = {
  manual: "#1976d2",
  next_contact: "#ed6c02",
  task: "#9c27b0",
  google: "#2e7d32",
};

export const AGENDA_STATUS_COLORS = {
  completed: "#607d8b",
  canceled: "#d32f2f",
};

export function getAgendaEventColors(theme) {
  return {
    manual: theme?.palette?.primary?.main || AGENDA_SOURCE_COLORS.manual,
    next_contact: theme?.palette?.warning?.main || AGENDA_SOURCE_COLORS.next_contact,
    task: theme?.palette?.secondary?.main || AGENDA_SOURCE_COLORS.task,
    google: theme?.palette?.success?.main || AGENDA_SOURCE_COLORS.google,
  };
}

export function getAgendaStatusColors(theme) {
  return {
    completed: theme?.palette?.text?.secondary || AGENDA_STATUS_COLORS.completed,
    canceled: theme?.palette?.error?.main || AGENDA_STATUS_COLORS.canceled,
  };
}

/**
 * FullCalendar `datesSet.end` is exclusive; map to inclusive day bounds for the API.
 */
export function toAgendaQueryBounds(from, to) {
  const start = from ? dayjs(from) : dayjs().startOf("month");
  let end = to ? dayjs(to) : dayjs().add(2, "month").endOf("day");
  if (
    end.isValid() &&
    end.hour() === 0 &&
    end.minute() === 0 &&
    end.second() === 0 &&
    end.millisecond() === 0
  ) {
    end = end.subtract(1, "millisecond");
  }
  return {
    fromStr: start.format("YYYY-MM-DD 00:00:00"),
    toStr: end.format("YYYY-MM-DD 23:59:59"),
  };
}

/** Keep invitations / own events / Google busy even if the calendar chip is off. */
export function isAgendaEventVisible(event, { visibleSources, calendarIds } = {}) {
  const source = event?.source || "manual";
  if (visibleSources && !visibleSources.has(source)) return false;
  if (source !== "manual" && source !== "google") return true;
  if (event?.meta?.busyOnly || event?.meta?.viewerIsOwner || event?.meta?.viewerIsAttendee) {
    return true;
  }
  const calendarId = Number(event?.calendarId || event?.meta?.calendarId);
  return !calendarId || !calendarIds || calendarIds.has(calendarId);
}

/** Normalize API datetimes for dayjs / FullCalendar (MySQL " " → ISO "T"). */
export function parseAgendaDateTime(value) {
  if (value == null || value === "") return null;
  if (value instanceof Date) return dayjs(value).isValid() ? dayjs(value) : null;
  const d = dayjs(String(value).trim().replace(" ", "T"));
  return d.isValid() ? d : null;
}

/** Mesma regra da API: fim explícito, senão +3h (ou próximo dia se for dia inteiro). */
export function resolveAgendaEventEffectiveEndAt(event) {
  const startAt = parseAgendaDateTime(event?.start);
  if (!startAt) return null;
  const endAt = parseAgendaDateTime(event?.end);
  if (endAt && endAt.isAfter(startAt)) return endAt;
  if (event?.allDay) return startAt.startOf("day").add(1, "day");
  return startAt.add(3, "hour");
}

export function isAgendaEventPast(event, now = dayjs()) {
  const endAt = resolveAgendaEventEffectiveEndAt(event);
  if (!endAt) return false;
  return !endAt.isAfter(now);
}

export function readAgendaEventStatus(event) {
  const raw = event && Object.prototype.hasOwnProperty.call(event, "status")
    ? event.status
    : event?.meta?.status;
  return raw === "completed" || raw === "canceled" ? raw : null;
}

export function mergeAgendaFeedEvent(current, updated) {
  if (!updated) return current || null;
  if (!current) return updated;
  const nextStatus = Object.prototype.hasOwnProperty.call(updated, "status")
    ? updated.status
    : readAgendaEventStatus(current);
  return {
    ...current,
    ...updated,
    status: nextStatus,
    meta: {
      ...(current.meta || {}),
      ...(updated.meta || {}),
      status: updated.meta && Object.prototype.hasOwnProperty.call(updated.meta, "status")
        ? updated.meta.status
        : nextStatus,
    },
  };
}

/**
 * FullCalendar timeGrid drops / misplaces timed events when `end` is missing or <= `start`.
 * Prefer omitting `end` so FC applies defaultTimedEventDuration.
 */
export function resolveAgendaEventEnd(start, end) {
  const startAt = parseAgendaDateTime(start);
  if (!startAt) return end || undefined;
  const endAt = parseAgendaDateTime(end);
  if (!endAt || !endAt.isAfter(startAt)) return undefined;
  return end;
}

export function agendaEventToFullCalendar(event, colors, statusColors) {
  const source = event.source || "manual";
  const status = readAgendaEventStatus(event);
  const sourceColor = colors?.[source] || AGENDA_SOURCE_COLORS[source] || AGENDA_SOURCE_COLORS.manual;
  const calendarColor =
    (source === "manual" || source === "google") && (event.calendarColor || event.meta?.calendarColor)
      ? event.calendarColor || event.meta?.calendarColor
      : null;
  const color = status
    ? statusColors?.[status] || AGENDA_STATUS_COLORS[status] || sourceColor
    : calendarColor || sourceColor;
  const end = resolveAgendaEventEnd(event.start, event.end);
  const isRecurring = Boolean(event.meta?.recurring || event.meta?.recurrenceRule || event.recurrence);
  return {
    id: String(event.id),
    title: event.title,
    start: event.start,
    end: end || undefined,
    allDay: Boolean(event.allDay),
    // Recurring series: edit via dialog only (drag would re-anchor the master incorrectly).
    editable: Boolean(event.editable) && !isRecurring,
    backgroundColor: color,
    borderColor: color,
    classNames: [
      ...(status ? [`agenda-status-${status}`] : []),
      ...(event.activeVideo ? ["agenda-active-video"] : []),
      ...(event.activeVideo?.isPrivate ? ["agenda-active-video-private"] : []),
      ...(isRecurring ? ["agenda-recurring"] : []),
    ],
    extendedProps: {
      source,
      status,
      meta: event.meta || {},
      calendarId: event.calendarId || event.meta?.calendarId,
      calendarName: event.calendarName || event.meta?.calendarName,
      calendarColor: event.calendarColor || event.meta?.calendarColor,
      statusEditable: Boolean(event.statusEditable),
      editable: Boolean(event.editable),
      recurring: isRecurring,
      activeVideo: event.activeVideo || null,
    },
  };
}

export function parseManualEventId(fcId) {
  const s = String(fcId || "");
  const m = s.match(/^manual:(\d+)(?::|$)/);
  return m ? Number(m[1]) : null;
}
